import * as DialogPrimitive from '@radix-ui/react-dialog'
import { ChevronDown, ChevronUp, X } from 'lucide-react'
import { cn } from '@/lib/utils/cn'

export type DrawerSize = 'standard' | 'wide' | 'xl'

export interface DrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 480 (standard) / 640 (wide — file preview) / 1100 (xl — split layouts). */
  /** Type scale for the drawer body. 'form' = the larger data-entry scale (tokens.css). */
  surface?: 'form'
  size?: DrawerSize
  /** Header id — monospace ident (SO-…, PO-…, Lot-…). */
  ident?: React.ReactNode
  title: React.ReactNode
  /** Metadata line beneath the title. */
  meta?: React.ReactNode
  /** Right-aligned header actions (buttons, chips). */
  headerActions?: React.ReactNode
  /** Prev/next callbacks — wires to `j`/`k` at the list level. */
  onPrev?: () => void
  onNext?: () => void
  /** Sticky footer actions. */
  footer?: React.ReactNode
  /**
   * When provided, the body becomes a 2-column split: `children` on the
   * left, `splitContent` on the right (with a vertical divider). Both
   * columns scroll independently. Header + footer span the full width.
   * Pair with `size="xl"` for usable column widths.
   */
  splitContent?: React.ReactNode
  children?: React.ReactNode
}

/**
 * Non-modal right-side overlay. Principle 4.3: doesn't dim the background,
 * doesn't trap focus aggressively, user can click the main content to
 * cancel. Slides in 200ms via CSS keyframes.
 *
 * Built on Radix Dialog with `modal={false}` so outside interaction is
 * allowed and no pointer-events overlay is injected.
 */
export function Drawer({
  open,
  onOpenChange,
  size = 'standard',
  surface,
  ident,
  title,
  meta,
  headerActions,
  onPrev,
  onNext,
  footer,
  splitContent,
  children,
}: DrawerProps) {
  const widthClass =
    size === 'xl' ? 'w-[1100px]' : size === 'wide' ? 'w-[640px]' : 'w-[480px]'
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          onInteractOutside={(e) => {
            // Non-modal: let outside clicks through to the main content,
            // and dismiss ourselves in the process.
            onOpenChange(false)
            e.preventDefault()
          }}
          onPointerDownOutside={(e) => e.preventDefault()}
          data-surface={surface}
          className={cn(
            'fixed right-0 top-0 z-drawer flex h-screen flex-col border-l border-border-default bg-surface shadow-lg',
            widthClass,
            'max-w-full',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-right duration-normal',
            'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right',
          )}
        >
          <header className="flex items-start gap-3 border-b border-border-subtle px-5 py-3">
            <div className="min-w-0 flex-1">
              {ident ? (
                <div className="text-role-metadata">{ident}</div>
              ) : null}
              <DialogPrimitive.Title asChild>
                <h3 className="truncate text-xl font-semibold leading-tight text-primary">
                  {title}
                </h3>
              </DialogPrimitive.Title>
              {meta ? (
                <div className="mt-1 flex items-center gap-2 text-2xs text-secondary">
                  {meta}
                </div>
              ) : null}
            </div>
            {(onPrev || onNext) && (
              <div className="flex gap-1">
                <button
                  type="button"
                  aria-label="Previous record"
                  onClick={onPrev}
                  disabled={!onPrev}
                  className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  type="button"
                  aria-label="Next record"
                  onClick={onNext}
                  disabled={!onNext}
                  className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronDown size={14} />
                </button>
              </div>
            )}
            {headerActions ? <div className="flex gap-2">{headerActions}</div> : null}
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                aria-label="Close drawer"
                className="inline-flex h-6 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
              >
                <X size={14} />
              </button>
            </DialogPrimitive.Close>
          </header>
          {splitContent ? (
            <div className="flex-1 min-h-0 grid grid-cols-[minmax(0,1fr)_minmax(360px,420px)]">
              <div className="overflow-y-auto px-5 py-4 min-w-0">
                {children}
              </div>
              <div className="overflow-y-auto border-l border-border-subtle bg-surface-sunk min-w-0 flex flex-col">
                {splitContent}
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          )}
          {footer ? (
            <footer className="flex items-center justify-end gap-2 border-t border-border-subtle bg-surface-sunk px-5 py-3">
              {footer}
            </footer>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
